// Compte d'administration des caisses (§26.4bis) — poser et faire TOURNER le mot de passe du parc.
//
// ⚠️ PANNEAU, PAS ÉCRAN : il vit dans un ONGLET de « Identités & accès », à côté des comptes staff.
// C'est le bon compromis entre deux exigences qui tiraient en sens inverse. Pour qui cherche « où gère-
// t-on les accès ? », il n'y a qu'UN endroit — une entrée de navigation dédiée à un compte unique était
// disproportionnée. Mais les deux tableaux restent SÉPARÉS, parce que ce ne sont pas les mêmes objets :
// un compte staff est une PERSONNE (identité, e-mail, session, rôle, 2FA déléguée à l'IdP), il y en a N
// et on les retire une par une ; celui-ci est UN SECRET PARTAGÉ, sans personne derrière, répliqué sur
// tout le parc, qu'on ne retire pas mais qu'on FAIT TOURNER. Les fondre dans une seule liste ferait
// courir le risque exact qu'on veut éviter : « retirer un utilisateur » et verrouiller, sans l'avoir
// voulu, toutes les tablettes en service.
//
// CE QUE CE PANNEAU REMPLACE. Jusqu'au 2026-08-09, ce mot de passe était écrit en dur dans le code de
// l'application : présent dans chaque APK, présent dans le dépôt Git, et impossible à changer sans
// publier une nouvelle version. Ici, en changer est une écriture — et le parc suit.
//
// ⚠️ CE QUE L'ÉCRAN NE FERA JAMAIS : afficher le mot de passe. La Console n'en garde qu'un HACHAGE, elle
// ne sait pas le relire. Perdu = on en pose un nouveau, il n'y a rien à « retrouver ».
//
// ⚠️ CE QU'IL DOIT DIRE, ET C'EST TOUT SON INTÉRÊT : « appliquée sur 42/47 caisses », avec le NOM des
// magasins qui n'ont pas reçu. Un « fait » global laisserait croire que l'ancien mot de passe ne
// fonctionne plus, alors qu'il ouvre encore les caisses restées hors ligne (§23.0).
import { useCallback, useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { dateTimeLong } from '@orgazone/date-format';
import { api, type AdminAccountState } from '../api';
import { Icon, showToast, useLatest, useOverlayPanel, type IconName } from '@orgazone/ui-web';
import { formPanelRef, Pill, Spin } from '../ui';

/** 8 caractères minimum, lettres et chiffres — la règle du serveur, redite ici pour ne pas la subir. */
const REGLE = /^[A-Za-z0-9]{8,}$/;

/**
 * État du compte, remonté dans un HOOK plutôt que gardé par le panneau.
 *
 * ⚠️ POURQUOI. Le bouton qui ouvre la modale vit dans l'EN-TÊTE de l'écran, avec « + Inviter un compte »
 * — tous les déclencheurs de modale au même endroit, c'est ce qui rend une console prévisible. Or son
 * libellé dépend de l'état (« Poser » s'il n'existe pas, « Changer » sinon) : l'en-tête a donc besoin de
 * cet état. Le hook le lui donne sans que le panneau cesse d'être l'auteur de la lecture.
 *
 * `actif` évite de lire quand l'onglet n'est pas ouvert — une requête de moins, rien de plus.
 *
 * ⚠️ CE COMMENTAIRE DISAIT AUTRE CHOSE, ET IL DÉCRIVAIT UN DÉFAUT : « cette route relance aussi la
 * DESCENTE de ce qui n'est pas encore parti ». C'était vrai — lire cet état distribuait le compte
 * d'administration à TOUTE la flotte — et `actif` servait à en limiter les dégâts. On ne borne plus un
 * effet de bord : on l'a retiré (retour utilisateur 2026-09-01). La descente est un GESTE — rotation,
 * création d'un magasin, ou le bouton « Renvoyer ».
 */
export function useAdminAccount(actif: boolean) {
  const [etat, setEtat] = useState<AdminAccountState | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const latest = useLatest();

  // ⚠️ L'échec de lecture est CONSERVÉ : sans lui, l'écran afficherait « aucun compte » alors que c'est
  // la requête qui a échoué — et on poserait un mot de passe en croyant qu'il n'y en avait pas.
  // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : la relecture qui suit un changement de mot de passe croise celle de
  //    l'ouverture de l'onglet — revenue la dernière, l'ancienne réafficherait l'état d'AVANT la rotation.
  const reload = useCallback(() => {
    const isLatest = latest();
    setLoading(true);
    api.adminAccount()
      .then((r) => { if (!isLatest()) return; setEtat(r); setErreur(null); })
      .catch((e) => { if (isLatest()) setErreur((e as Error).message); })
      .finally(() => { if (isLatest()) setLoading(false); });
  }, [latest]);
  useEffect(() => { if (actif) reload(); }, [actif, reload]);

  return { etat, erreur, loading, reload };
}
export type AdminAccountHook = ReturnType<typeof useAdminAccount>;

// ── Modale : poser ou faire tourner le mot de passe (même patron que « Ajouter un membre ») ───────
// ⚠️ Une MODALE et pas un formulaire dans la page, à dessein : cette saisie change l'accès de TOUTES
// les caisses du parc. Un champ posé au fil de la page se remplit distraitement ; une modale demande
// de l'ouvrir, donc de le vouloir. C'est aussi ce que fait déjà l'ajout d'un membre, et deux gestes de
// même gravité doivent se ressembler.
export function ChangerMotDePasse({ existe, libelleActuel, onClose, onDone }: {
  existe: boolean;
  libelleActuel: string;
  onClose: () => void;
  onDone: (notice: string, tone: 'ok' | 'warn') => void;
}) {
  const [mdp, setMdp] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [libelle, setLibelle] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Le focus va au premier champ par ce socle, jamais par `autoFocus` : posé avant lui, il serait retenu comme déclencheur.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!REGLE.test(mdp)) { setErr('8 caractères minimum, lettres et chiffres uniquement.'); return; }
    if (mdp !== confirmation) { setErr('Les deux saisies diffèrent.'); return; }
    setBusy(true);
    try {
      const r = await api.setAdminAccount({ password: mdp, ...(libelle.trim() ? { label: libelle.trim() } : {}) });
      // Le compte-rendu est CHIFFRÉ, jamais « enregistré » : c'est la seule chose qui dit si l'ancien
      // mot de passe fonctionne encore quelque part.
      if (r.failures.length === 0) {
        onDone(`Mot de passe appliqué sur ${r.delivered}/${r.total} magasin(s) — rotation n°${r.rotation}.`, 'ok');
      } else {
        onDone(`Rotation n°${r.rotation} enregistrée, appliquée sur ${r.delivered}/${r.total} magasin(s) — ${r.failures.length} n'ont pas reçu, l'ancien mot de passe y fonctionne encore.`, 'warn');
      }
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>{existe ? 'Changer le mot de passe des caisses' : 'Poser le mot de passe des caisses'}</h3>
        <label htmlFor="aa-mdp">Mot de passe <span className="muted">(8 caractères minimum, lettres et chiffres)</span></label>
        <input id="aa-mdp" type="password" value={mdp} onChange={(e) => setMdp(e.target.value)} required autoComplete="new-password" />
        <label htmlFor="aa-conf">Confirmation</label>
        <input id="aa-conf" type="password" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} required autoComplete="new-password" />
        <label htmlFor="aa-lib">Nom affiché à la connexion <span className="muted">(facultatif)</span></label>
        <input id="aa-lib" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder={libelleActuel} />
        {err && <div className="err">{err}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-cta" disabled={busy}>
            {busy ? 'Envoi au parc…' : existe ? 'Changer et faire descendre' : 'Poser et faire descendre'}
          </button>
        </div>
        <p className="note">⚠️ Il descendra vers <strong>tous</strong> les magasins en service. Une caisse hors ligne
          conservera l’ancien mot de passe jusqu’à sa prochaine connexion — l’écran vous dira lesquelles.
          La Console ne conserve qu’une empreinte : <strong>ce mot de passe ne pourra plus être affiché</strong>.</p>
      </form>
    </div>
  );
}

/** 4 chiffres, exactement — la règle du serveur, redite ici pour ne pas la subir. */
const MOBILE_PIN_RULE = /^\d{4}$/;

// ── Modale : poser ou faire tourner le PIN de Sliderz Mobile Partner ─────────────────────────────
// ⚠️ MÊME GRAVITÉ, MÊME GESTE que le mot de passe : une modale qu'on ouvre en le voulant, une confirmation,
// et un compte rendu chiffré. Le PIN a SA rotation : le changer ne touche pas le mot de passe des caisses.
export function ChangeMobilePin({ exists, onClose, onDone }: {
  exists: boolean;
  onClose: () => void;
  onDone: (notice: string, tone: 'ok' | 'warn') => void;
}) {
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  // Le focus va au premier champ par ce socle, jamais par `autoFocus` : posé avant lui, il serait retenu comme déclencheur.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  const titleId = useId();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!MOBILE_PIN_RULE.test(pin)) { setError('Exactement 4 chiffres.'); return; }
    if (pin !== confirmation) { setError('Les deux saisies diffèrent.'); return; }
    setBusy(true);
    try {
      const r = await api.setAdminMobilePin({ pin });
      if (r.failures.length === 0) {
        onDone(`PIN Mobile Partner appliqué sur ${r.delivered}/${r.total} magasin(s) — rotation n°${r.rotation}.`, 'ok');
      } else {
        onDone(`Rotation n°${r.rotation} du PIN enregistrée, appliquée sur ${r.delivered}/${r.total} magasin(s) — ${r.failures.length} n'ont pas reçu, l'ancien PIN y fonctionne encore.`, 'warn');
      }
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modalwrap" onClick={onClose}>
      <form className="modal" ref={formPanelRef(panelRef)} role="dialog" aria-modal="true" aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3 id={titleId}>{exists ? 'Changer le PIN Mobile Partner' : 'Poser le PIN Mobile Partner'}</h3>
        <label htmlFor="aa-pin">PIN <span className="muted">(4 chiffres)</span></label>
        <input id="aa-pin" type="password" inputMode="numeric" maxLength={4} value={pin}
          onChange={(e) => setPin(e.target.value)} required autoComplete="new-password" />
        <label htmlFor="aa-pin-conf">Confirmation</label>
        <input id="aa-pin-conf" type="password" inputMode="numeric" maxLength={4} value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)} required autoComplete="new-password" />
        {error && <div className="err">{error}</div>}
        <div className="modalfoot">
          <button type="button" className="ghost btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-cta" disabled={busy}>
            {busy ? 'Envoi au parc…' : exists ? 'Changer et faire descendre' : 'Poser et faire descendre'}
          </button>
        </div>
        <p className="note">⚠️ Il ouvre le compte d’administration sur <strong>tous</strong> les terminaux Mobile Partner du parc,
          et il est indépendant du mot de passe des caisses. Quatre chiffres se retrouvent vite à partir d’un terminal
          volé : <strong>changez-le dès qu’un terminal est perdu</strong>. La Console n’en garde qu’une empreinte — il ne
          pourra plus être affiché.</p>
      </form>
    </div>
  );
}

/**
 * CE RETARD SE RÉSOUDRA-T-IL TOUT SEUL ?
 *
 * ⚠️ « store_unknown » N'EST PAS UN RETARD, c'est une ABSENCE. Le back-office ne connaît que les
 * magasins qui ont une base ; un magasin encore en mise en service n'en a pas, donc aucune caisse ne
 * tirera jamais chez lui. Le ranger avec les retardataires faisait promettre une résolution qui ne
 * pouvait pas venir — et invitait explicitement à ne rien faire.
 */
const estInjoignable = (erreur?: string | null): boolean => !!erreur && erreur.includes('store_unknown');

/**
 * Un fait d'une tuile : un libellé discret au-dessus d'une valeur lisible.
 * ⚠️ « — » GRISÉ QUAND IL N'Y A RIEN : une case vide se lit comme un oubli d'affichage, un tiret dit « rien ».
 */
function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const empty = value === '—';
  return (
    <div>
      <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{label}</div>
      <div className={mono ? 'mono' : undefined} style={{ fontSize: mono ? 12.5 : 13, fontWeight: 600, color: empty ? 'var(--ink400)' : 'var(--ink900)', marginTop: 3 }}>{value}</div>
    </div>
  );
}

/** Un encart d'une tuile — l'information qui se lit AVANT d'agir. */
function Callout({ tone, children }: { tone: 'info' | 'warn' | 'danger'; children: ReactNode }) {
  const color = tone === 'danger' ? 'var(--da-fg)' : tone === 'warn' ? 'var(--wa-fg)' : 'var(--in-fg)';
  return (
    <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', border: '1px solid var(--ink200)', background: 'var(--ink-50)', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 'var(--t-dense)', color: 'var(--ink600)', lineHeight: 1.55 }}>
      <span style={{ color, display: 'grid', marginTop: 1 }}><Icon name={tone === 'info' ? 'info' : 'alert'} size={15} /></span>
      <span>{children}</span>
    </div>
  );
}

/**
 * UNE TUILE PAR SECRET — ce qu'il ouvre, où il se saisit, où il en est, et ce qu'il faut savoir.
 *
 * ⚠️ POURQUOI UNE TUILE ET PAS UNE LIGNE (retour utilisateur) : le PIN de Mobile Partner ouvre un
 * compte d'administration sur TOUS les terminaux du parc. Une ligne « posé · rotation n°2 » ne disait
 * ni ce qu'il ouvre, ni où il se tape, ni ce qu'il coûte — c'est pourtant ce qu'on doit savoir AVANT de
 * le poser. La tuile le dit, et porte le geste qui le change à côté de ce qu'il change.
 */
function SecretTile({ icon, product, title, does, entry, reservedId, state, facts, callouts, action }: {
  icon: IconName;
  product: string;
  title: string;
  does: ReactNode;
  entry: string;
  reservedId: string;
  state: { label: string; tone: 'ok' | 'danger' | 'muted' };
  facts: { label: string; value: string }[];
  callouts: ReactNode;
  action: ReactNode;
}) {
  const pill = state.tone === 'ok'
    ? { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' }
    : state.tone === 'danger' ? { bg: 'var(--da-bg)', fg: 'var(--da-fg)' } : { bg: 'var(--ink-50)', fg: 'var(--ink500)' };
  return (
    <section className="dcard" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--ink-50)', display: 'grid', placeItems: 'center', color: 'var(--ink600)', flexShrink: 0 }}>
          <Icon name={icon} size={20} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow">{product}</div>
          <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, marginTop: 2 }}>{title}</div>
        </div>
        <Pill bg={pill.bg} fg={pill.fg}>{state.label}</Pill>
      </div>

      <div>
        <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink500)', marginBottom: 4 }}>Ce qu’il ouvre</div>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink700)', lineHeight: 1.6 }}>{does}</div>
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 6 }}>{entry}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px 18px', paddingTop: 12, borderTop: '1px solid var(--ink100)' }}>
        <Fact label="Identifiant réservé" value={reservedId} mono />
        {facts.map((f) => <Fact key={f.label} label={f.label} value={f.value} />)}
      </div>

      {callouts}

      {action && <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>{action}</div>}
    </section>
  );
}

/**
 * Le panneau du compte d'administration : une tuile par secret, puis la diffusion au parc.
 * Il montre l'état, il ne le lit pas (voir `useAdminAccount`) ; les modales restent montées par l'écran.
 */
export function AdminAccountPanel({ etat, erreur, loading, reload, canWrite, onChangePassword, onChangeMobilePin }: AdminAccountHook & {
  canWrite: boolean;
  onChangePassword: () => void;
  onChangeMobilePin: () => void;
}) {
  // ⚠️ LES HOOKS AVANT TOUT RETOUR : ce composant sort tôt (chargement, erreur de lecture), et un
  //    `useState` placé après ces sorties ne serait pas appelé au même rang à chaque rendu.
  const [renvoi, setRenvoi] = useState(false);

  /**
   * Renvoie le compte COURANT aux magasins qui ne l'ont pas reçu.
   * ⚠️ Le résultat dit « 42/47 », jamais « fait » : une caisse hors ligne gardera l'ancien secret
   *    jusqu'à sa prochaine synchronisation, et l'écran ne doit pas laisser croire l'inverse (§23.0).
   */
  const renvoyer = async () => {
    setRenvoi(true);
    try {
      const r = await api.pushAdminAccount();
      showToast(`Renvoyé à ${r.delivered}/${r.total} magasin(s).`, r.failures?.length ? 'warn' : 'ok');
      reload();
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 'err');
    } finally { setRenvoi(false); }
  };

  if (loading && !etat) return <div><Spin size={16} /> Lecture…</div>;

  if (erreur) {
    return (
      <div>
        <div className="err">État illisible : {erreur}<br />Rien ne prouve qu’un compte existe ou non — ne posez pas de secret à l’aveugle.</div>
      </div>
    );
  }

  const exists = !!etat?.exists;
  const pin = etat?.mobilePin;
  const pinSet = exists && !!pin?.exists;
  const enRetard = etat?.pending ?? [];
  const changedBy = (at?: string | null, by?: string | null): string =>
    at ? `${dateTimeLong(at)}${by ? ` · ${by}` : ''}` : '—';

  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        Le compte d’administration de l’éditeur, valable sur <strong>tout le parc</strong>. Il porte <strong>un secret par outil</strong> :
        chaque secret ouvre un seul outil, se change sans toucher l’autre, et aucune autre source ne peut les écrire.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 14, alignItems: 'stretch', marginBottom: 14 }}>
        <SecretTile
          icon="tablet"
          product="Sliderz Retail"
          title="Mot de passe des caisses"
          does={<>Le compte <strong>ADMIN</strong> sur <strong>toutes les caisses</strong> du parc : réglages, outils de maintenance,
            déblocage d’un vendeur verrouillé. Chaque ouverture laisse une trace au journal de la caisse.</>}
          entry="Se saisit au clavier de la tablette — 8 caractères minimum, lettres et chiffres."
          reservedId="ADMIN"
          state={!exists ? { label: 'non posé', tone: 'danger' }
            : etat?.isActive === false ? { label: 'désactivé', tone: 'danger' }
            : { label: `posé · rotation n°${etat?.rotation}`, tone: 'ok' }}
          facts={[
            { label: 'Dernier changement', value: exists ? changedBy(etat?.rotatedAt, etat?.rotatedBy) : '—' },
            { label: 'Portée', value: 'toutes les caisses' },
          ]}
          callouts={!exists ? (
            <Callout tone="danger">
              <strong>Tant qu’il n’est pas posé, aucune caisse ne reçoit de compte d’administration</strong> — et une tablette
              fraîchement mise en service affiche « Aucun compte » à la connexion.
            </Callout>
          ) : (
            <Callout tone="info">Le changer ne touche pas le PIN des terminaux Mobile Partner.</Callout>
          )}
          action={canWrite && (
            <button type="button" className="btn btn-cta sm" onClick={onChangePassword}>
              {exists ? 'Changer le mot de passe' : 'Poser le mot de passe'}
            </button>
          )}
        />

        <SecretTile
          icon="barcode"
          product="Sliderz Mobile Partner"
          title="PIN des terminaux"
          does={<>La tuile <strong>ADMIN</strong> — « Administration Orgazone » — sur <strong>tous les terminaux</strong> du parc :
            inventaire, réception, transfert, consultation du stock, et création d’article depuis un code inconnu.
            Le support voit exactement ce que voit l’opérateur.</>}
          entry="Se tape sur le pavé du terminal, comme le PIN d’un vendeur — 4 chiffres."
          reservedId="ADMIN_MP"
          state={!exists ? { label: 'compte à poser d’abord', tone: 'muted' }
            : pinSet ? { label: `posé · rotation n°${pin?.rotation}`, tone: 'ok' }
            : { label: 'non posé', tone: 'danger' }}
          facts={[
            { label: 'Dernier changement', value: pinSet ? changedBy(pin?.rotatedAt, pin?.rotatedBy) : '—' },
            { label: 'Portée', value: 'tous les terminaux' },
          ]}
          callouts={
            !exists ? (
              <Callout tone="info">Le PIN est le second secret du compte : posez d’abord le mot de passe des caisses.</Callout>
            ) : (
              <>
                {!pinSet && (
                  <Callout tone="danger">
                    <strong>Tant qu’il n’est pas posé, aucun terminal ne reçoit de compte d’administration</strong> : la tuile
                    ADMIN n’apparaît sur aucun Mobile Partner.
                  </Callout>
                )}
                {/* ⚠️ LE RISQUE SE DIT DANS LA TUILE, pas seulement dans la modale : c'est ici qu'on revient
                    le jour où un terminal disparaît, et c'est ce jour-là qu'il faut lire cette phrase. */}
                <Callout tone="warn">
                  <strong>Quatre chiffres valables sur tout le parc.</strong> Ils se retrouvent vite à partir d’un terminal volé :
                  changez-le dès qu’un terminal est perdu. Le changer ne touche pas le mot de passe des caisses.
                </Callout>
              </>
            )
          }
          action={canWrite && exists && (
            <button type="button" className="btn btn-cta sm" onClick={onChangeMobilePin}>
              {pinSet ? 'Changer le PIN' : 'Poser le PIN'}
            </button>
          )}
        />
      </div>

      {/* ── DIFFUSION AU PARC ─────────────────────────────────────────────────────────────────── */}
      {/* ⚠️ UN SEUL BLOC POUR LES DEUX SECRETS, parce que la livraison se suit par COUPLE : un magasin a reçu
          la dernière version du compte — mot de passe ET PIN — ou il ne l'a pas reçue. */}
      {exists && (
        <section className="block">
          <div className="blockhead">
            <strong>Diffusion au parc</strong>
            <Pill bg={enRetard.length ? 'var(--da-bg)' : 'var(--ok-bg)'} fg={enRetard.length ? 'var(--da-fg)' : 'var(--ok-fg)'}>
              {etat?.delivered}/{etat?.stores} magasin(s)
            </Pill>
          </div>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink600)' }}>
            Un magasin a reçu la <strong>dernière version du compte</strong> — mot de passe et PIN — ou ne l’a pas reçue.
            Chaque appareil l’applique à sa synchronisation suivante.
          </div>

          {/* ⚠️ Les retardataires sont NOMMÉS. « 42/47 » sans savoir lesquels ne permet de rien décider. */}
          {enRetard.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="err" style={{ marginBottom: 8 }}>
                Ces magasins n’ont pas encore reçu la dernière version du compte — <strong>l’ancien secret y fonctionne encore</strong>.
                {enRetard.some((m) => !estInjoignable(m.error)) && ' Ceux qui sont simplement en retard la recevront à leur prochaine synchronisation.'}
              </div>
              {/* ⚠️ DEUX RETARDS QUI N'ONT RIEN À VOIR. Les confondre faisait dire « rien à refaire » sur
                  un magasin qui n'allait JAMAIS recevoir la rotation : sans base, il n'a pas de caisse,
                  donc aucune synchronisation à venir. Celui-là demande un geste, et l'écran doit le
                  nommer plutôt que de rassurer. */}
              {enRetard.some((m) => estInjoignable(m.error)) && (
                <div className="err" style={{ marginBottom: 8 }}>
                  <strong>Certains ne la recevront jamais sans intervention</strong> : ils sont inconnus du
                  back-office, donc sans base et sans caisse. Mettez-les en service depuis leur fiche, ou
                  retirez-les — attendre ne changera rien.
                </div>
              )}
              {/* ⚠️ LE RENVOI EST UN BOUTON : on décide, on trace, et on peut ne pas le faire. La simple
                  LECTURE de cet état ne distribue jamais rien. */}
              <button type="button" className="ghost btn-ghost" disabled={renvoi}
                onClick={() => { void renvoyer(); }}
                style={{ width: 'auto', padding: '6px 12px', fontSize: 'var(--t-note)', marginBottom: 8 }}>
                {renvoi ? 'Renvoi…' : 'Renvoyer aux magasins en attente'}
              </button>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 'var(--t-body)', lineHeight: 1.7 }}>
                {enRetard.map((m) => (
                  <li key={m.gln}>
                    <span className="mono">{m.gln}</span>{m.name ? ` — ${m.name}` : ''}
                    {m.error && <span className="muted"> · {m.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
